import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Button } from "../Button";
import { Input } from "../Field";
import f from "../Field/Field.module.scss";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import s from "./InlineEditRow.module.scss";

type Props = {
  value: string;
  /** The input's name, read out only ("Rename Holidays"). */
  label: string;
  /** Gets the trimmed text. Resolve when saved (the page then closes the row); reject to keep the row open. */
  onSave: (v: string) => Promise<void>;
  onCancel: () => void;
  /** Each change of the text (e.g. to drop the page's own `error` once the name is being changed). */
  onChange?: (v: string) => void;
  /** Checked before saving (trimmed text): a message refuses it at once, without calling `onSave`; typing clears it. */
  validate?: (v: string) => string | undefined;
  saveLabel: string;
  cancelLabel: string;
  maxLength: number;
  error?: string;
  /** Shown when `onSave` rejects and the page gives no `error` of its own ("The new name wasn't saved. Try again."). */
  failedLabel: string;
  /**
   * Where focus goes after a save or a cancel. Default: the element that had focus when the row opened (the trigger).
   * Pass a getter when the page re-renders the trigger (e.g. a row's ⋯ button replaced by this row).
   */
  returnFocusTo?: () => HTMLElement | null | undefined;
};

/**
 * Board 30's rename row: a text input with Cancel and Save. Opens focused with the text selected; Enter saves, Escape
 * cancels (and stays inside: a dialog around it does not close). Enter during IME composition is ignored. Unchanged
 * text is a cancel; empty text cannot be saved. One save at a time, the input read-only meanwhile. The message (the
 * page's `error`, or `failedLabel` after a rejection with none) is an alert of its own.
 */
export const InlineEditRow = ({ value, label, onSave, onCancel, onChange, validate, saveLabel, cancelLabel, maxLength, error, failedLabel, returnFocusTo }: Props) => {
  const id = useId();
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [invalid, setInvalid] = useState<string>();
  const message = error || invalid || (failed ? failedLabel : undefined);
  const errId = message ? `${id}-err` : undefined;
  const empty = !draft.trim();
  const input = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    const active = document.activeElement;
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    input.current?.focus();
    input.current?.select();
    return () => { alive.current = false; };
  }, []);

  // After the page has re-rendered (the row closed), put focus back on the trigger.
  const restoreFocus = () => setTimeout(() => {
    const el = returnFocusTo?.() ?? opener.current;
    if (el?.isConnected) el.focus();
  }, 0);

  const cancel = () => { onCancel(); restoreFocus(); };

  const save = async () => {
    if (saving || empty) return;
    const next = draft.trim();
    if (next === value) { cancel(); return; }
    const refusal = validate?.(next);
    if (refusal) { setInvalid(refusal); input.current?.focus(); return; }
    setSaving(true);
    setFailed(false);
    try {
      await onSave(next);
      restoreFocus();
    } catch {
      // The page shows why (via `error`); the row stays open with the text kept.
      if (alive.current) { setFailed(true); input.current?.focus(); }
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return; // an input method is still composing the word
    if (e.key === "Enter") { e.preventDefault(); void save(); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (!saving) cancel(); }
  };

  return (
    <div className={s.wrap}>
      <div className={s.row}>
        <label htmlFor={id} className={VISUALLY_HIDDEN}>{label}</label>
        <Input ref={input} id={id} value={draft} maxLength={maxLength} invalid={!!message} aria-describedby={errId} dir="auto"
          readOnly={saving} className={s.input} onChange={(e) => { setDraft(e.target.value); setFailed(false); setInvalid(undefined); onChange?.(e.target.value); }} onKeyDown={onKeyDown} />
        <span className={s.actions}>
          <Button onClick={cancel} disabled={saving}>{cancelLabel}</Button>
          <Button variant="primary" onClick={() => void save()} loading={saving} disabled={empty}>{saveLabel}</Button>
        </span>
      </div>
      {message && <p id={errId} role="alert" className={f.error}><ErrorOutlineRoundedIcon aria-hidden className={f.errIcon} />{message}</p>}
    </div>
  );
};
